/**
 * Export formats for playtest notes: the tuning values (#119's one line, and
 * JSON) and the session log of a game (readable text, and JSON).
 */
import {
  MOTION_DEFAULTS,
  WAVE_TUNABLES,
  type MotionConfig,
} from "../components/board/motionConfig";
import { longestStreak, type GameLog } from "./sessionLog";

export interface LogMeta {
  graphVersion: string | null;
  graphHash: string | null;
  build: string;
  sandbox: boolean;
}

/** All wave values as one pasteable line (the same line as #119). */
export function settingsLine(m: MotionConfig): string {
  const parts = WAVE_TUNABLES.map((t) => `${t.key}=${m[t.key]}`);
  return `wave: ${parts.join(" ")} waveEasing=${m.waveEasing}`;
}

/** The same values as JSON, plus just the ones that differ from defaults. */
export function settingsJson(m: MotionConfig): string {
  const wave: Record<string, number | string> = {};
  const changed: Record<string, number | string> = {};
  const keys = [...WAVE_TUNABLES.map((t) => t.key), "waveEasing" as const];
  for (const k of keys) {
    wave[k] = m[k];
    if (m[k] !== MOTION_DEFAULTS[k]) changed[k] = m[k];
  }
  return JSON.stringify({ wave, changed }, null, 2);
}

const secs = (ms: number) => `+${(ms / 1000).toFixed(1)}s`;

function header(meta: LogMeta): string {
  const graph = `graph ${meta.graphVersion ?? "?"} (${meta.graphHash ?? "?"})`;
  const sandbox = `sandbox ${meta.sandbox ? "on" : "off"}`;
  return `Synapse session log · ${graph} · build ${meta.build} · ${sandbox}`;
}

/** The game's moves, one per line, for playtest notes. */
export function formatSessionLog(game: GameLog | null, meta: LogMeta): string {
  if (!game) return `${header(meta)}\nno game recorded yet`;
  const lines = [
    header(meta),
    `game: ${game.start} → ${game.target} · ${game.mode} · par ${game.par} · started ${new Date(game.startedAt).toISOString()}`,
  ];
  let n = 0;
  const kinds: { isGlobalOptimal: boolean; isLocalOptimal: boolean }[] = [];
  for (const e of game.entries) {
    const at = secs(e.t - game.startedAt);
    if (e.type === "backtrack") {
      lines.push(`    ${at}  backtrack to ${e.to}`);
      kinds.splice(Math.max(0, kinds.length - 1));
      continue;
    }
    n++;
    kinds.push({
      isGlobalOptimal: e.kind === "gold",
      isLocalOptimal: e.kind === "purple",
    });
    const kind =
      e.kind === "detour" && e.goldNext
        ? `detour (gold path: ${e.goldNext})`
        : e.kind;
    lines.push(
      `${String(n).padStart(2)}  ${at}  ${e.from} → ${e.choice}  ${kind}  streak ${e.streak}  ${e.source}  options: ${e.options.join(", ")}`,
    );
  }
  const r = game.result;
  lines.push(
    r
      ? `result: ${r.status === "won" ? "won" : "gave up"} in ${r.moves} moves (par ${game.par}) · longest streak ${longestStreak(kinds)} · ${secs(r.t - game.startedAt)}`
      : "result: in progress",
  );
  return lines.join("\n");
}

export function sessionLogJson(game: GameLog | null, meta: LogMeta): string {
  return JSON.stringify({ meta, game }, null, 2);
}
